import { Pencil, Trash2 } from "lucide-react";
import { MEASUREMENT_TYPES } from "../../../config/enums/MeasurementTypes";
import { convertStringUnitToNumeric } from "../../../utils/utils";
import { Badge } from "../../ui/common/badge";
import { Button } from "../../ui/common/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/common/tooltip";
import { IWasteRecord } from "../wasteManagement.types";
import { convertWeightValue, normalizeWasteUnit } from "../wasteManagement.utils";

interface WasteRecordCardProps {
  field: any;
  globalIndex: number;
  recordData: IWasteRecord | undefined;
  managementTypeOptions: Array<{ value: string; text: string; label: string }>;
  sideStreamUnit: "kg" | "ton";
  onEdit: (id: string | number) => void;
  onDelete: (index: number, id: string | number, name: string) => void;
  disabled: boolean;
  lockStatus: boolean;
}

/**
 * Renders a single waste/side-stream record in its compact, read-only view.
 */
export const WasteRecordCard = ({
  field,
  globalIndex,
  recordData,
  managementTypeOptions,
  sideStreamUnit,
  onEdit,
  onDelete,
  disabled,
  lockStatus
}: WasteRecordCardProps) => {
  if (!field._id) return null;

  const selectedManagementType = managementTypeOptions.find(
    t => t.value === String(recordData?.managementTypeId)
  );

  const recordUnit = normalizeWasteUnit(recordData?.unit, MEASUREMENT_TYPES.WEIGHT_KG);
  const displayUnit = convertStringUnitToNumeric(sideStreamUnit);
  const rawDistribution = Number(recordData?.sideStreamsDistribution || 0);
  const convertedDistributionValue =
    recordUnit !== displayUnit
      ? convertWeightValue(rawDistribution, recordUnit, displayUnit)
      : rawDistribution;

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-3 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50">
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-flex-wrap">
        <span className="tw-font-semibold tw-text-muted-foreground tw-w-5">{globalIndex + 1}</span>
        <span className="tw-font-medium tw-text-foreground">{recordData?.managementName || "—"}</span>
        {recordData?.managementTypeId && (
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {selectedManagementType?.text || ""}
          </Badge>
        )}
        <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
          {convertedDistributionValue}{" "}
          {sideStreamUnit}
        </span>
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
              onClick={() => onEdit(field._id)}
              disabled={disabled || lockStatus}
            >
              <Pencil className="tw-h-3 tw-w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="tw-text-xs">Edit</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Button
        variant="ghost"
        size="icon"
        className="tw-h-7 tw-w-7 tw-text-destructive"
        onClick={() => onDelete(globalIndex, field._id, recordData?.managementName || "")}
        disabled={disabled || lockStatus}
      >
        <Trash2 className="tw-h-3 tw-w-3" />
      </Button>
    </div>
  );
};
